/**
 * Sheet management executors — create_sheet, delete_sheet, rename_sheet.
 *
 * Each export is a CoreFn: (ctx, args?) => Promise<unknown> | unknown
 * that queues operations on proxy objects. The outer `executeExcelTool`
 * wrapper calls `context.sync()` after the core function returns.
 */

/* global Excel */

// ---------------------------------------------------------------------------
// create_sheet
// ---------------------------------------------------------------------------

/**
 * Create a new worksheet in the workbook.
 *
 * Expected args:
 *   name?:     string — Optional name for the new sheet
 *   position?: number — Zero-based position to insert at
 */
export async function createSheetCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const name = args?.name as string | undefined;
  const position = args?.position as number | undefined;

  const newSheet = ctx.workbook.worksheets.add(name);
  newSheet.load("name,position");

  if (position !== undefined) {
    newSheet.position = position;
  }

  await ctx.sync();

  return {
    success: true,
    name: newSheet.name,
    position: newSheet.position,
  };
}

// ---------------------------------------------------------------------------
// delete_sheet
// ---------------------------------------------------------------------------

/**
 * Delete a worksheet from the workbook.
 *
 * Expected args:
 *   name: string — The exact name of the worksheet to delete
 */
export function deleteSheetCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const name = args?.name as string | undefined;

  if (!name) {
    throw new Error("delete_sheet requires a `name` argument.");
  }

  const sheet = ctx.workbook.worksheets.getItem(name);
  sheet.delete();

  return { success: true, deletedSheet: name };
}

// ---------------------------------------------------------------------------
// rename_sheet
// ---------------------------------------------------------------------------

/**
 * Rename an existing worksheet.
 *
 * Expected args:
 *   currentName: string — The current name of the sheet
 *   newName:     string — The new name for the sheet
 */
export function renameSheetCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const currentName = args?.currentName as string | undefined;
  const newName = args?.newName as string | undefined;

  if (!currentName) {
    throw new Error("rename_sheet requires a `currentName` argument.");
  }
  if (!newName) {
    throw new Error("rename_sheet requires a `newName` argument.");
  }

  const sheet = ctx.workbook.worksheets.getItem(currentName);
  sheet.name = newName;

  return { success: true, previousName: currentName, newName };
}
